/**
 * WeakSignalsPanel — Sprint 3 V3
 * ================================
 * Affiche les signaux faibles détectés cette semaine (cron lundi 6h)
 * Inséré dans le dashboard admin (sous le DailyInsightHero).
 */
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Activity, AlertTriangle, AlertCircle, Info, RefreshCw, Sparkles } from 'lucide-react';

const SEVERITY = {
    urgent: { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', icon: AlertTriangle, label: 'Urgent' },
    attention: { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertCircle, label: 'À surveiller' },
    info: { color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200', icon: Info, label: 'Info' },
};

export default function WeakSignalsPanel() {
    const [signals, setSignals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);

    const fetchSignals = async () => {
        try {
            setLoading(true);
            const res = await api.get('/ai/weak-signals');
            setSignals(res.data || []);
        } catch (e) {
            // silent fail
        } finally {
            setLoading(false);
        }
    };

    const triggerScan = async () => {
        try {
            setScanning(true);
            await api.post('/ai/weak-signals/scan', {});
            await fetchSignals();
        } catch (e) {
            // silent
        } finally {
            setScanning(false);
        }
    };

    useEffect(() => {
        fetchSignals();
    }, []);

    if (loading) return null;
    if (signals.length === 0) {
        return (
            <div className="bg-white border border-slate-200 rounded-2xl p-5 text-sm text-slate-500 flex items-center gap-3" data-testid="weak-signals-empty">
                <div className="p-2 bg-emerald-100 rounded-lg">
                    <Activity className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="flex-1">
                    <div className="text-sm font-medium text-slate-800">Aucun signal faible détecté</div>
                    <div className="text-xs text-slate-500">L'IA scanne votre agence chaque lundi à 6h pour détecter les signaux invisibles.</div>
                </div>
                <button
                    onClick={triggerScan}
                    disabled={scanning}
                    className="text-xs text-slate-600 hover:text-slate-900 underline"
                    data-testid="weak-signals-scan"
                >
                    {scanning ? 'Scan en cours...' : 'Scanner maintenant'}
                </button>
            </div>
        );
    }

    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="weak-signals-panel">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">Signaux faibles détectés</h3>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Patterns invisibles dans vos dashboards — repérés par l'IA Homly</p>
                </div>
                <button
                    onClick={triggerScan}
                    disabled={scanning}
                    className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-50 rounded-lg"
                    title="Re-scanner"
                    data-testid="weak-signals-rescan"
                >
                    <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
                </button>
            </div>
            <div className="space-y-3">
                {signals.map((s) => {
                    const conf = SEVERITY[s.severity] || SEVERITY.info;
                    const Icon = conf.icon;
                    return (
                        <div
                            key={s.id}
                            className={`flex gap-3 ${conf.bg} ${conf.border} border rounded-xl p-3.5`}
                            data-testid={`weak-signal-${s.type}`}
                        >
                            <div className={`shrink-0 p-2 bg-white rounded-lg ${conf.color}`}>
                                <Icon className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-[10px] font-bold uppercase tracking-wider ${conf.color}`}>{conf.label}</span>
                                </div>
                                <p className="text-sm font-semibold text-slate-900 leading-tight mb-1">{s.headline}</p>
                                <p className="text-xs text-slate-600 leading-relaxed">{s.evidence}</p>
                                {s.recommendation && (
                                    <p className="text-xs text-slate-700 mt-2 pl-3 border-l-2 border-slate-300 italic">
                                        💡 {s.recommendation}
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
